import { useEffect, useRef } from 'react';

/** Breathing room kept between the focused element and the pane's edge, in px. */
const EDGE_MARGIN = 24;

/**
 * Scroll position that brings `target` into view inside `pane`, on one axis.
 *
 * Returns the current offset untouched when the target is already comfortably
 * inside the pane — otherwise it centres it, which is what makes a jump between
 * two far-apart changes readable. `Math.max` covers a target taller than the
 * pane: centring one would push its start off the top, so it aligns to the edge
 * instead. Both values are deltas off the live scroll offset, so this is
 * direction-agnostic and works unchanged in RTL.
 */
function axisOffset(
  current: number,
  paneStart: number,
  paneSize: number,
  targetStart: number,
  targetSize: number,
): number {
  const start = targetStart - paneStart;
  const end = start + targetSize;
  if (start >= EDGE_MARGIN && end <= paneSize - EDGE_MARGIN) return current;
  return current + start - Math.max(EDGE_MARGIN, (paneSize - targetSize) / 2);
}

/**
 * Keeps the focused change visible in a scrollable pane.
 *
 * Selection moves from three places that never touch the document — the rail's
 * change list, the prev/next buttons and the A/R/arrow shortcuts — so without
 * this a reviewer decides on a change they cannot see. Clicking a mark in the
 * document is the fourth, and that one is already on screen: the scroll only
 * runs when the target actually sits outside the pane, so a click never yanks
 * the text out from under the cursor.
 *
 * Attach `scrollRef` to the scrollport and `targetRef` to the *selected*
 * element only (`ref={selected ? targetRef : undefined}`), so the pane always
 * holds a ref to the one thing worth scrolling to.
 *
 * `selectionKey` is what re-runs the scroll: usually the selected change's id,
 * but a pane whose target can move for another reason (a spreadsheet switching
 * sheets) should fold that into the key, or it scrolls to a stale element.
 */
export function useScrollToSelected<
  Pane extends HTMLElement = HTMLDivElement,
  Target extends HTMLElement = HTMLElement,
>(selectionKey: string | null) {
  const scrollRef = useRef<Pane>(null);
  const targetRef = useRef<Target>(null);

  useEffect(() => {
    const pane = scrollRef.current;
    const target = targetRef.current;
    if (!pane || !target || !selectionKey) return;

    const p = pane.getBoundingClientRect();
    const t = target.getBoundingClientRect();
    const top = axisOffset(pane.scrollTop, p.top, p.height, t.top, t.height);
    const left = axisOffset(pane.scrollLeft, p.left, p.width, t.left, t.width);
    if (top === pane.scrollTop && left === pane.scrollLeft) return;

    pane.scrollTo({
      top,
      left,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    });
  }, [selectionKey]);

  return { scrollRef, targetRef };
}
